import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { colorToArray, i18nIntl, isColorBright, isEmpty, isNotEmpty, RGB_2_HEX } from '../../lib/ep.js';
import { useCanvasMachine } from '../../ts/games/canvasStateMachine.ts';

import GuessScoreCanvas from '../games/GuessScoreCanvas.js';
import { clientFinalPage } from '../games/StageFinalMessages.js';

import MisconfiguredGameWarning from './MisconfiguredGameWarning.js';
import SimpleCanvasContainer from './SimpleCanvasContainer.js';

import ActionButton from '../builder/ActionButton.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import { localizeFields } from '../builder/DynamicFields.js';

import OneTimeBracketFinal from './OneTimeBracketFinal.js';
import OneTimeBracketCanvas from '../games/OneTimeBracketCanvas.js';
import { getFullImage } from './GuessScoreClient.js';
import ActionLink from '../builder/ActionLink.js';
import { useCSS } from '../../lib/hooks.js';

export const checkMatchesFirstRound = (matches, num_rounds) => {
	const min_matches = Math.pow(2, num_rounds) / 2 - 1;

	const wrong_matches = matches.filter((match, m) => {
		if(m > min_matches) return false;

		if(isEmpty(match, 'id_1') || isEmpty(match, 'id_2')) return true;
		return false;
	});

	return wrong_matches.length;
};

const NavLinks = forwardRef(({ ...props }, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props.stage.Stage ?? false;
	const teams = props.teams ?? [];

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const [ actual_match, setActualMatch ] = useState(0);
	const [ finish_enabled, setFinishEnabled ] = useState(false);
	const [ button_disabled, setButtonDisabled ] = useState(false);

	const total_matches = props?.total_matches ?? 15;

	const goFinishGame = () => {
		if(is_builder && !is_test) return;

		setButtonDisabled(true);
		props.gameCanvas.current.finishGame((total_matches < 2) ? false : true);
	};

	const resetGame = () => {
		if(props.onReset == undefined) return;
		props.onReset();
	};

	useImperativeHandle(ref, () => ({
		goNextMatch(v) {
			setActualMatch(v);
		},

		enableFinish(winner_team_id) {
			setTimeout(() => {
				setButtonDisabled(false);
				setFinishEnabled(winner_team_id);
			}, 500);
		},
	}), []);

	if(stage === false) return null;
	if(teams.length == 0) return null;

	const classes = ['guess_score_buttons', 'floating_actions', 'gui_flex_col'];
	if(finish_enabled !== false) classes.push('visible_actions');

	const main_color = colorToArray(stage.main_color, true);
	const text_color = colorToArray(stage.text_color, true);

	const winner_team = (finish_enabled !== false) ? teams.find((t) => (t.id == finish_enabled)) : false;

	const is_main_color_bright = (isColorBright(RGB_2_HEX(`${main_color[0]}, ${main_color[1]}, ${main_color[2]}, 1`))) ? true : false;

	const button_styles = {
		'--button-background-color':main_color,
		'--button-background-color-hover':`${main_color[0]}, ${main_color[1]}, ${main_color[2]}, 0.7`,
		'--button-text-color':`${(is_main_color_bright) ? '0, 0, 0, 1' : '255, 255, 255, 1'}`,
		'--button-text-color-hover':'var(--button-text-color)',
		'--link-color':'var(--button-background-color)',
		'--link-color-hover':'var(--button-background-color-hover)',
		'--main-color-sec':`${main_color[0]}, ${main_color[1]}, ${main_color[2]}`,
		'--text-color':text_color,
		'--p-color':text_color,
		'--h1-color':text_color,
		'--h2-color':text_color,
		'--h3-color':text_color,
	};

	return (
		<div class={classes.join(" ")} style={button_styles}>
			<div class="bracket_winner">
				<strong class="knk-winners icon" />
				
				<h2>
					<strong>{winner_team.name}</strong>
				</h2>
			</div>

			<ButtonCollection
				_class={{ other:'flex_button' }}
			>
				<ActionButton
					_fullclass=''
					icon='gui_nav_link in_top'
					onClick={goFinishGame}
					disabled={button_disabled}
				>Confirm</ActionButton>
			</ButtonCollection>

			<ActionLink
				onClick={resetGame}
			>Reset</ActionLink>
		</div>
	)
});

const OneTimeBracketClient = ({ stage, ...props }) => {

	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_reseting, setIsReseting ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const num_rounds = parseInt(stage?.Stage?.guess_score_num_rounds ?? '4');
	const _matches = JSON.parse(JSON.stringify(stage?.Stage?.guess_score_matches ?? []));

	const matches = [];
	_matches.map((match, m) => {
		if((is_builder && !is_test) || m < Math.pow(2, num_rounds) / 2) {
			matches.push(match);
			return;
		}

		matches.push({
			...match,
			id_1:'',
			id_2:'',
		});
	});

	const nav_ref = useRef({});
	const header_ref = useRef({});
	const reset_iteration = useRef(stage.Stage?.reset ?? -2);

	const promotion = props?.promotion?.Promotion ?? {};

	const teams = localizeFields((promotion?.guess_score_teams ?? []), props.locale);
	const rounds = localizeFields(JSON.parse(JSON.stringify(stage?.Stage?.guess_score_rounds ?? [])), props.locale);

	useEffect(() => {
		if(!is_builder && !is_test) return;
		if(reset_iteration.current == -2) {
			reset_iteration.current = -1;
			return;
		}

		const new_iteration = stage.Stage?.reset ?? 0;
		setIsReseting(true);
		reset_iteration.current = parseInt(new_iteration);

		setTimeout(() => {
			setIsReseting(false);
		}, 100);
	}, [ stage.Stage.reset ]);

	rounds.map((round, r) => {
		if(isEmpty(round, 'title')) rounds[r].title = `${i18n("Round")} #${r + 1}`;
	});

	const findTeamById = (id) => {
		return teams.find((t) => (t.id == id));
	};

	const resetGame = () => {
		setIsReseting(true);
		setTimeout(() => {
			setIsReseting(false);
		}, 100);
	};

	const canvas_images = () => {
		const images = [];

		if(isNotEmpty(stage.Stage, 'guess_score_bracket_bg_image')) {
			images.push({
				id:'bracket_bg_image',
				name:stage.Stage.guess_score_bracket_bg_image,
				url:stage.Stage.full_guess_score_bracket_bg_image,
				fit:'cover',
			})
		}

		if(isNotEmpty(stage.Stage, 'bg_image')) {
			images.push({
				id:'bg_image',
				name:stage.Stage.bg_image,
				url:stage.Stage.full_bg_image,
				fit:'cover',
			})
		}

		matches.map((match) => {
			const team_1 = findTeamById(match.id_1);
			const team_2 = findTeamById(match.id_2);

			if(team_1 != undefined && (isNotEmpty(team_1,'image') || isNotEmpty(team_1,'external_id'))) {
				images.push({
					id:`logo_${team_1.id}`,
					name:(isNotEmpty(team_1, 'short_name')) ? team_1.short_name : team_1.name,
					url:getFullImage(team_1),
					fit:'contain',
					get_average_color:true,
				});

				images.push({
					id:`logo_${team_1.id}_small`,
					name:`${(isNotEmpty(team_1, 'short_name')) ? team_1.short_name : team_1.name} (small)`,
					url:getFullImage(team_1, 'small'),
					fit:'contain',
				});
			}

			if(team_2 != undefined && (isNotEmpty(team_2,'image') || isNotEmpty(team_2,'external_id'))) {
				images.push({
					id:`logo_${team_2.id}`,
					name:(isNotEmpty(team_2, 'short_name')) ? team_2.short_name : team_2.name,
					url:getFullImage(team_2),
					fit:'contain',
					get_average_color:true,
				});

				images.push({
					id:`logo_${team_2.id}_small`,
					name:`${(isNotEmpty(team_2, 'short_name')) ? team_2.short_name : team_2.name} (small)`,
					url:getFullImage(team_2, 'small'),
					fit:'contain',
				});
			}
		});

		return images;
	};

	const [ state, dispatch ] = useCanvasMachine({
		images:canvas_images,
		images_dependencies:[
			stage.Stage.guess_score_bracket_bg_image,
			stage.Stage.guess_score_matches,
		],
	});

	const default_props = {
		indexed_images:state.images,
		loaded_status:state.state,
		locale:props?.locale,
	};

	const fake_stats = [];
	const stats = props?.stats ?? ((!is_builder || is_test) ? [] : fake_stats);

	if(state.state != 'LOADED') return null;
	if(is_reseting) return null;

	const min_teams = Math.pow(2, num_rounds);
	const missing_matches = checkMatchesFirstRound(matches, num_rounds);

	let missing_anything = false;
	let missing_anything_text = "";
	if(teams.length < min_teams) {
		missing_anything = 'teams';
		missing_anything_text = props.intl.formatMessage({ id:"Create at least {num} teams" }, { num:min_teams });
	} else if(missing_matches > 0) {
		missing_anything = 'matches';
		missing_anything_text = (missing_matches > 1) ? props.intl.formatMessage({ id:"There are still {num} uncomplete matches in first round" }, { num:missing_matches }) : i18n("There is still 1 uncomplete match in first round");
	}

	const final_stage = clientFinalPage(stage, props.actual_state);

	const default_bg_color = promotion?.layout_props?.color?.background_color ?? 'rgba(255, 255, 255, 1)';
	const default_text_color = promotion?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const default_main_color = promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';

	if(isNotEmpty(final_stage.Stage, 'main_description')) final_stage.Stage.main_description = null;
	if(isEmpty(final_stage.Stage, 'bg_color')) final_stage.Stage.bg_color = default_bg_color;
	if(isEmpty(final_stage.Stage, 'text_color')) final_stage.Stage.text_color = default_text_color;
	if(isEmpty(final_stage.Stage, 'main_color')) final_stage.Stage.main_color = default_main_color;

	/* Hidden instructions message */
	final_stage.Stage.guess_score_step_2_instructions = i18n("Select the winner");
	/*if(isEmpty(final_stage.Stage, 'guess_score_step_2_instructions')) {
		final_stage.Stage.guess_score_step_2_instructions = i18n("Select the winner");
	}*/

	return (
		<SimpleCanvasContainer
			{...props}
			stage={final_stage}
			header_image={final_stage.Stage.full_guess_score_sponsors_image}
			final_page={<OneTimeBracketFinal
				{...default_props}
				stats={stats}
			/>}
			extra_content={<NavLinks
				ref={nav_ref}
				stage={final_stage}
				teams={teams}
				onReset={resetGame}
				total_matches={matches.length}
				is_builder={is_builder}
				is_test={is_test}
				intl={props.intl}
			/>}
			game_type='bracket_predictor'
			force_game_to_square={(missing_anything !== false) ? true : false}
		>
			{(missing_anything === false) ? (
				<GuessScoreCanvas
					{...default_props}
					teams={teams}
					rounds={rounds}
					matches={matches}
					buttons={nav_ref}
					header={header_ref}
					bracket={<OneTimeBracketCanvas
						{...default_props}
						teams={teams}
						matches={matches}
					/>}
				/>
			) : (
				<MisconfiguredGameWarning
					message={missing_anything_text}
					autotranslate={false}
				/>
			)}
		</SimpleCanvasContainer>
	)
};

export default injectIntl(OneTimeBracketClient);